import { createRouter, createWebHistory } from "vue-router"
import { isUserAllowed, authApi, getSession, getUser } from "@/compositions/utils.ts"

const levels = { admin: [1], pm: [1, 2, 4], translator: [1, 2, 3, 4], developer: [1, 4, 10], logged_in_user: [1, 2, 3, 4, 10], aoa: [1, 2, 4, 20], test: [5] }

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition // Scroll to the saved position (e.g., when using browser's back/forward buttons)
    } else {
      return { top: 0 } // Scroll to the top of the page
    }
  },
  routes: [
    {
      path: "/",
      name: "home",
      component: () => import("../views/HomeView.vue"),
      meta: {
        title: "Babble-on Dashboard",
        user_level: levels.pm,
      },
    },
    {
      path: "/quotes",
      name: "quotes",
      meta: {
        title: "Quotes",
        user_level: levels.admin,
      },
      component: () => import("../views/QuotesView.vue"),
    },
    {
      path: "/quotes/:id",
      name: "quote",
      meta: {
        title: "Quote",
        user_level: levels.admin,
      },
      component: () => import("../views/QuotesView.vue"),
      props: true,
    },
    {
      path: "/jobs/:user?",
      name: "jobs",
      meta: {
        title: "Jobs",
        user_level: levels.translator,
      },
      component: () => import("../views/TranslatorHome.vue"),
      props: true,
    },
    {
      path: "/job/:id/:language?",
      name: "job",
      meta: {
        title: "View Job",
      },
      component: () => import("../views/JobView.vue"),
      props: true,
      // beforeEnter(to, from, next) {
      //   // Set a unique param to force reload
      //   if (!to.query.tk) {
      //     next({ ...to, query: { ...to.query, tk: Date.now().toString().substring(10) } })
      //   } else {
      //     next()
      //   }
      // },
    },
    {
      path: "/job/:id/edit",
      name: "editjob",
      meta: {
        title: "Edit Job",
        user_level: levels.pm,
      },
      component: () => import("../views/JobView.vue"),
      // component: async () => {
      //   if (isUserAllowed(levels.pm)) return import("../views/JobView.vue")
      //   else return import("../views/Safe.vue")
      // },
      props: (route) => ({ id: route.params.id, editing: true }),
    },
    {
      path: "/job/:id/new",
      name: "newjob",
      meta: {
        title: "New Job",
        user_level: levels.pm,
      },
      component: () => import("../views/JobView.vue"),
      props: (route) => ({ id: route.params.id, new: true, editing: true }),
    },
    {
      path: "/job/new",
      name: "chooseinvoice",
      meta: {
        title: "New Job by invoice",
        user_level: levels.pm,
      },
      component: () => import("../components/JobView/Editing/ChooseInvoice.vue"),
    },

    {
      path: "/calendar/:user?",
      name: "translatorCalendar",
      meta: {
        title: "Calendar",
        user_level: levels.translator,
      },
      component: () => import("../views/TranslatorCalendarView.vue"),
      props: true,
    },
    {
      path: "/invoices/:customerinvoice?",
      name: "invoices",
      meta: {
        title: "Invoices",
        user_level: levels.pm,
      },
      component: () => import("../views/InvoiceList.vue"),
      props: true,
    },
    {
      path: "/accounting",
      name: "accounting",
      meta: {
        title: "Accounting",
        user_level: levels.admin,
      },
      component: () => import("../views/AccountingView.vue"),
      props: true,
    },
    {
      path: "/myinvoices/:user?",
      name: "myinvoices",
      meta: {
        title: "Invoices",
        user_level: levels.translator,
      },
      component: () => import("../views/TranslatorInvoicesView.vue"),
      props: true,
    },
    {
      path: "/translatorinvoices",
      redirect: "/accounting",
    },
    {
      path: "/admin/users",
      name: "useradmin",
      meta: {
        title: "User Administration",
        user_level: levels.admin,
      },
      component: () => import("../views/UserAdminView.vue"),
    },
    {
      path: "/admin/backups",
      name: "backups",
      meta: {
        title: "DB Backups",
        user_level: levels.admin,
      },
      component: () => import("../views/BackupsView.vue"),
    },
    {
      path: "/invoice/:id",
      name: "viewinvoice",
      meta: {
        title: "Invoice",
        // user_level: levels.translator,
      },
      component: () => import("../views/TranslatorInvoice.vue"),
      props: true,
    },

    {
      path: "/clients/:client",
      name: "clients",
      meta: {
        title: "Client",
        user_level: levels.pm,
      },
      component: () => import("../views/ClientView.vue"),
      props: true,
    },
    {
      path: "/glossary/:defaultGlossary?",
      name: "glossary",
      meta: {
        title: "Glossary",
      },
      component: () => import("../views/Glossary.vue"),
      props: true,
    },
    {
      path: "/appstore-translator",
      name: "appstore-translator",
      meta: {
        title: "App Store Description Translator",
        user_level: levels.admin, // demo only — admins
      },
      component: () => import("../views/AppStoreTranslator.vue"),
    },
    {
      path: "/clientinvoice/:id?",
      name: "clientinvoice",
      meta: {
        title: "Babble-on Invoice",
        user_level: levels.pm,
      },
      component: () => import("../views/ModifyInvoice.vue"),
      props: true,
    },
    {
      path: "/clientinvoice/:dupinvoice/:source",
      name: "duplicateinvoice",
      meta: {
        title: "Duplicate invoice",
        user_level: levels.pm,
      },
      component: () => import("../views/ModifyInvoice.vue"),
      props: (route) => ({ dupinvoice: route.params.dupinvoice, source: route.params.source }),
    },
    {
      path: "/printinvoice/:id",
      name: "printinvoice",
      meta: {
        title: "Print Invoice",
        user_level: levels.developer,
      },
      component: () => import("../views/PrintInvoice.vue"),
      props: true,
    },
    {
      path: "/pdf/:id",
      name: "pdfinvoice",
      meta: {
        title: "Invoice",
      },
      component: () => import("../views/PdfInvoice.vue"),
      props: true,
    },

    {
      path: "/i/:hash",
      name: "customerinvoice",
      meta: {
        title: "Babble-on Invoice",
      },
      component: () => import("../views/CustomerInvoiceView.vue"),
      props: true,
    },
    {
      path: "/f/:id",
      name: "clientdelivery",
      meta: {
        title: "Babble-on Delivery",
      },
      component: () => import("../views/ClientDelivery.vue"),
      props: true,
    },

    {
      path: "/timeclock/:user?",
      name: "timeclock",
      meta: {
        title: "Time Clock",
        user_level: levels.pm,
      },
      component: () => import("../views/TimeClockView.vue"),
    },
    {
      path: "/developer",
      name: "developer-home",
      meta: {
        title: "Developer Portal",
        user_level: levels.developer,
      },
      component: () => import("../views/DeveloperHomeView.vue"),
    },
    {
      path: "/developer/request",
      name: "request",
      meta: {
        title: "Request Translation",
        user_level: levels.developer,
      },
      component: () => import("../views/DeveloperHomeView.vue"),
    },
    {
      path: "/developer/:username",
      name: "developer-view",
      meta: {
        title: "Developer Portal",
        user_level: levels.pm,
      },
      component: () => import("../views/DeveloperHomeView.vue"),
      props: true,
    },
    {
      path: "/developer/:username/request",
      name: "request-view",
      meta: {
        title: "Request Translation",
        user_level: levels.pm,
      },
      component: () => import("../views/DeveloperHomeView.vue"),
      props: true,
    },
    {
      path: "/developer/:username/files",
      name: "developer-files",
      meta: {
        title: "Developer Files",
        user_level: levels.pm,
      },
      component: () => import("../views/DeveloperFilesView.vue"),
      props: true,
    },
    {
      path: "/pseudo",
      name: "pseudo",
      meta: {
        title: "Cost estimation and Pseudolocalization",
        user_level: levels.pm,
      },
      component: () => import("../views/PseudoView.vue"),
    },
    {
      path: "/aoa",
      name: "aoa",
      meta: {
        title: "Academy of Art Translations",
        user_level: levels.aoa,
      },
      component: () => import("../views/AoaView.vue"),
    },
    {
      path: "/logout",
      name: "logout",
      meta: {
        title: "Signing out…",
      },
      component: () => import("../views/LogoutView.vue"),
    },
    {
      path: "/login",
      name: "login",
      meta: {
        title: "Sign in to Babble-on",
      },
      component: () => import("../views/LoginView.vue"),
    },
    {
      path: "/signup",
      name: "signup",
      meta: {
        title: "Sign up for Babble-on",
      },
      component: () => import("../views/SignUpView.vue"),
    },
    {
      path: "/profile/:user?",
      name: "profile",
      meta: {
        title: "My Profile",
        user_level: levels.logged_in_user,
      },
      component: () => import("../views/ProfileView.vue"),
      props: true,
    },
    {
      path: "/review/:jobid/:language?",
      name: "review",
      meta: {
        title: "Screenshot Review",
        user_level: levels.translator,
      },
      component: () => import("../views/ReviewView.vue"),
      props: true,
    },
    {
      path: "/upload-review/:jobid",
      name: "developer-review",
      meta: {
        title: "Upload screenshots",
        user_level: levels.developer,
      },
      component: () => import("../views/DeveloperReviewView.vue"),
      props: true,
    },
    {
      path: "/viewer",
      name: "viewer",
      meta: {
        title: "AWS viewer",
        user_level: levels.pm,
      },
      props: true,
      component: () => import("../views/AWSViewer.vue"),
    },
    {
      path: "/oops",
      name: "oops",
      meta: {
        title: "Oops",
      },
      component: () => import("../views/403View.vue"),
    },
    {
      path: "/safe",
      name: "safe",
      meta: {
        title: "Safe",
      },
      component: () => import("../views/Safe.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      redirect: (to) => ({ name: "oops", query: { error: "404", page: to.fullPath } }),
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  if (to.meta?.title) document.title = to.meta.title as string

  // Check if the route has a permission requirement
  if (to.meta?.user_level) {
    const allowed = await isUserAllowed(to.meta.user_level as number[])
    if (allowed === true) {
      next()
    } else if (allowed === "login") {
      console.warn(`[router] Redirecting to login: route=${to.fullPath}, from=${from.fullPath}, required_level=${JSON.stringify(to.meta.user_level)}`)
      next({ name: "login", query: { to: to.fullPath } })
    } else {
      // User is logged in but doesn't have permission. Rather than bounce
      // translators/developers to a 403 on the PM-only home page, send them to
      // the landing page that matches their role.
      if (to.name === "home") {
        const levels = (await getUser())?.data?.user_level?.map((l: string) => parseInt(l)) ?? []
        if (levels.includes(3)) return next({ name: "jobs" })
        if (levels.includes(10)) return next({ name: "developer-home" })
      }
      next({ name: "oops", query: { error: "403", page: to.fullPath, required: JSON.stringify(to.meta.user_level) } })
    }
  } else {
    next()
  }
})

// Fire-and-forget page view recording. Only logged-in users — anonymous
// customer-invoice / delivery views are ignored because they have no user_id.
router.afterEach((to) => {
  if (!getSession()) return
  // For /oops we tack the intended page onto the recorded path so the
  // admin sees *where* the user was trying to go, not just that they bounced.
  let path = to.path
  if (to.name === "oops" && to.query?.page) {
    // Server strips at '?' (strtok), so trim the intended page's query first.
    const intended = String(to.query.page).split("?")[0]
    path = `/oops → ${intended}`
  } else if (to.query?.tab) {
    // Tabbed pages (e.g. the developer portal) keep the same path but switch
    // tabs via ?tab=. The server strips at '?', so fold the tab into the path
    // with '#' to record each tab as its own page-view row.
    path = `${to.path}#${to.query.tab}`
  }
  authApi("recordPageView", { path, route_name: to.name ? String(to.name) : "" }).catch(() => {})
})

export default router
